<script lang="ts">
  import { onDestroy } from 'svelte';
  import { liveInvitesByEventStore } from '../../lib/convexRealtime';
  import {
    createPersonalInvite,
    regenerateInvite,
    revokeInvite,
    deleteInvite,
    bulkEnsurePersonalInvites,
    fetchPersonalInvitesExport,
    type PersonalInvitePayload
  } from '../../lib/convexApi';
  import type {
    AdminPersonRecord,
    InviteRecord,
    BulkPersonalInviteResult,
    PersonalInviteExportRow
  } from '../../lib/types';

  export let eventId: string;
  export let people: AdminPersonRecord[] = [];

  let inviteStore = liveInvitesByEventStore(eventId);
  let currentEventId = eventId;
  let invitesState = { data: [] as InviteRecord[], loading: true, error: null as string | null };
  const unsubscribe = inviteStore.subscribe(state => {
    invitesState = {
      data: Array.isArray(state.data) ? state.data : [],
      loading: state.loading,
      error: state.error
    };
  });
  void inviteStore.refresh();

  let invites: InviteRecord[] = [];
  let plusOneInvites: InviteRecord[] = [];
  let personalInvites: InviteRecord[] = [];
  $: invites = invitesState.data ?? [];
  $: plusOneInvites = invites.filter(invite => invite.kind === 'plusOne');
  $: personalInvites = invites.filter(invite => invite.kind === 'personal');

  let selectedPersonId = '';
  let creatingInvite = false;
  let actionPending = false;
  let actionMessage: string | null = null;
  let actionError: string | null = null;
  let bulkBusy = false;
  let exportBusy = false;

  let personLookup = new Map<string, AdminPersonRecord['person']>();
  let personalCandidates: AdminPersonRecord[] = [];
  let selectedPerson: AdminPersonRecord['person'] | null = null;
  let selectedRecord: AdminPersonRecord | null = null;
  let selectedTicketId: string | null = null;

  $: personLookup = new Map(people.map(record => [record.person.id, record.person]));
  $: personalCandidates = people.filter(record => !record.person.externalId);
  $: {
    if (personalCandidates.length === 0) {
      selectedPersonId = '';
    } else if (!selectedPersonId || !personalCandidates.some(record => record.person.id === selectedPersonId)) {
      selectedPersonId = personalCandidates[0].person.id;
    }
  }
  $: selectedPerson = selectedPersonId ? personLookup.get(selectedPersonId) ?? null : null;
  $: selectedRecord = selectedPersonId ? personalCandidates.find(record => record.person.id === selectedPersonId) ?? null : null;
  $: selectedTicketId = selectedRecord
    ? (selectedRecord.tickets.find(ticket => ticket.eventId === eventId && ticket.status !== 'cancelled')?.id ?? null)
    : null;

  $: if (inviteStore && eventId && eventId !== currentEventId) {
    currentEventId = eventId;
    void inviteStore.setArgs({ eventId });
    void inviteStore.refresh();
  }

  onDestroy(() => {
    unsubscribe();
    inviteStore.dispose();
  });

  function resetMessages() {
    actionMessage = null;
    actionError = null;
  }

  async function refreshInvites() {
    try {
      await inviteStore.refresh();
    } catch (error) {
      console.error('Failed to refresh invites', error);
    }
  }

  function buildInviteUrl(code: string): string {
    if (typeof window === 'undefined') {
      return `?invite=${encodeURIComponent(code)}`;
    }
    const origin = window.location.origin;
    return `${origin}?invite=${encodeURIComponent(code)}`;
  }

  function summarizeBulkResult(result?: BulkPersonalInviteResult | null): string {
    if (!result) {
      return 'No invites were generated. Guests may already be covered.';
    }
    const parts: string[] = [];
    if (result.created > 0) {
      parts.push(`${result.created} new ${result.created === 1 ? 'invite' : 'invites'} created`);
    }
    if (result.keptActive > 0) {
      parts.push(`${result.keptActive} ${result.keptActive === 1 ? 'guest' : 'guests'} already had invites`);
    }
    if (result.replacedRevoked > 0) {
      parts.push(`${result.replacedRevoked} revoked ${result.replacedRevoked === 1 ? 'invite' : 'invites'} replaced`);
    }
    if (result.skipped > 0) {
      parts.push(`${result.skipped} skipped (linked accounts or missing data)`);
    }
    if (parts.length === 0) {
      return 'No invites were generated. Guests may already be covered.';
    }
    return parts.join(' · ');
  }

  function formatCsvValue(value: string | number | null): string {
    if (value === null || value === undefined) return '';
    const stringValue = String(value);
    if (/[",\n\r]/.test(stringValue)) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  }

  function buildInviteCsv(rows: PersonalInviteExportRow[]): string {
    const header = [
      'Guest name',
      'Guest email',
      'Guest phone',
      'Ticket code',
      'Ticket status',
      'Invite code',
      'Invite status',
      'Invite issued at',
      'Invite used count',
      'Invite link'
    ];
    const lines = [header.map(formatCsvValue).join(',')];
    for (const row of rows) {
      const issuedAt = typeof row.issuedAt === 'number' && row.issuedAt > 0 ? new Date(row.issuedAt).toISOString() : '';
      const inviteLink = buildInviteUrl(row.code);
      const line = [
        row.guestName ?? '',
        row.guestEmail ?? '',
        row.guestPhone ?? '',
        row.ticketCode ?? '',
        row.ticketStatus ?? '',
        row.code,
        row.status,
        issuedAt,
        row.usedCount,
        inviteLink
      ].map(value => formatCsvValue(value as string | number | null));
      lines.push(line.join(','));
    }
    return lines.join('\n');
  }

  async function handleBulkEnsureInvites() {
    if (!eventId) return;
    if (bulkBusy) return;
    bulkBusy = true;
    resetMessages();
    try {
      const result = await bulkEnsurePersonalInvites(eventId);
      actionMessage = summarizeBulkResult(result);
      await refreshInvites();
    } catch (error) {
      actionError = error instanceof Error ? error.message : 'Failed to generate invites.';
    } finally {
      bulkBusy = false;
    }
  }

  async function handleExportInvites() {
    if (!eventId) return;
    if (exportBusy) return;
    exportBusy = true;
    resetMessages();
    try {
      const rows = await fetchPersonalInvitesExport(eventId);
      if (rows.length === 0) {
        actionMessage = 'No personal invites available to export.';
        return;
      }
      const csv = buildInviteCsv(rows);
      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `personal-invites-${eventId}-${timestamp}.csv`;
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
      actionMessage = `Downloaded ${rows.length} personal invite${rows.length === 1 ? '' : 's'}.`;
    } catch (error) {
      console.error('Failed to export invites', error);
      actionError = error instanceof Error ? error.message : 'Failed to export invites.';
    } finally {
      exportBusy = false;
    }
  }

  async function handleCreatePersonalInvite() {
    if (!eventId) return;
    if (!selectedPerson) {
      actionError = 'Select a guest before creating an invite.';
      return;
    }
    creatingInvite = true;
    resetMessages();
    try {
      const payload: PersonalInvitePayload = {
        eventId,
        targetPersonId: selectedPerson.id,
        targetTicketId: selectedTicketId ?? undefined
      };
      const result = await createPersonalInvite(payload);
      if (!result) {
        throw new Error('Invite could not be created.');
      }
      actionMessage = 'Personal invite created.';
      await refreshInvites();
    } catch (error) {
      actionError = error instanceof Error ? error.message : 'Failed to create invite.';
    } finally {
      creatingInvite = false;
    }
  }

  async function handleRegenerate(invite: InviteRecord) {
    actionPending = true;
    resetMessages();
    try {
      await regenerateInvite(invite.code);
      actionMessage = 'Invite regenerated.';
      await refreshInvites();
    } catch (error) {
      actionError = error instanceof Error ? error.message : 'Failed to regenerate invite.';
    } finally {
      actionPending = false;
    }
  }

  async function handleRevoke(invite: InviteRecord) {
    actionPending = true;
    resetMessages();
    try {
      await revokeInvite(invite.code);
      actionMessage = 'Invite revoked.';
      await refreshInvites();
    } catch (error) {
      actionError = error instanceof Error ? error.message : 'Failed to revoke invite.';
    } finally {
      actionPending = false;
    }
  }

  async function handleDelete(invite: InviteRecord) {
    actionPending = true;
    resetMessages();
    try {
      const success = await deleteInvite(invite.code);
      if (!success) {
        throw new Error('Invite could not be deleted.');
      }
      actionMessage = 'Invite deleted.';
      await refreshInvites();
    } catch (error) {
      actionError = error instanceof Error ? error.message : 'Failed to delete invite.';
    } finally {
      actionPending = false;
    }
  }

  async function copyToClipboard(text: string, label: string) {
    resetMessages();
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      actionMessage = `${label} copied.`;
    } catch (error) {
      console.error('Clipboard copy failed', error);
      actionError = 'Copy failed. Try again.';
    }
  }

  function invitePerson(invite: InviteRecord) {
    if (!invite.targetPersonId) return null;
    return personLookup.get(invite.targetPersonId) ?? null;
  }

  function inviteLink(invite: InviteRecord): string {
    return buildInviteUrl(invite.code);
  }
</script>

<section class="invite-manager">
  <header>
    <div>
      <h3>Invites</h3>
      <p>Monitor guest codes and issue personal invites directly from the control room.</p>
    </div>
    <button type="button" on:click={refreshInvites} disabled={invitesState.loading}>Refresh</button>
  </header>

  <div class="bulk-tools">
    <button type="button" on:click={handleBulkEnsureInvites} disabled={bulkBusy}>
      {bulkBusy ? 'Generating invites…' : 'Generate invites for missing guests'}
    </button>
    <button type="button" class="ghost" on:click={handleExportInvites} disabled={exportBusy}>
      {exportBusy ? 'Preparing export…' : 'Download invite list'}
    </button>
  </div>

  {#if invitesState.error}
    <p class="error-text">{invitesState.error}</p>
  {/if}
  {#if actionError}
    <p class="error-text">{actionError}</p>
  {/if}
  {#if actionMessage}
    <p class="notice-text">{actionMessage}</p>
  {/if}

  <div class="invite-form">
    <h4>Create personal invite</h4>
    {#if personalCandidates.length === 0}
      <p class="empty-text">Add a guest without a linked account to issue a personal invite.</p>
    {:else}
      <label>
        <span>Select guest</span>
        <select bind:value={selectedPersonId}>
          {#each personalCandidates as record (record.person.id)}
            <option value={record.person.id}>
              {record.person.name}
              {#if record.person.email}
                · {record.person.email}
              {:else}
                · No email on file
              {/if}
            </option>
          {/each}
        </select>
      </label>
      <button type="button" class="cta" on:click={handleCreatePersonalInvite} disabled={creatingInvite}>
        {creatingInvite ? 'Creating invite…' : 'Create invite'}
      </button>
    {/if}
  </div>

  <div class="invite-section">
    <h4>Plus-one invites</h4>
    {#if invitesState.loading && plusOneInvites.length === 0}
      <p>Loading invites…</p>
    {:else if plusOneInvites.length === 0}
      <p class="empty-text">No plus-one invites issued yet.</p>
    {:else}
      <table>
        <thead>
          <tr>
            <th>Code</th>
            <th>Usage</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {#each plusOneInvites as invite (invite.id)}
            <tr>
              <td>
                <strong>{invite.code}</strong>
              </td>
              <td>{invite.usedCount} / {invite.maxUses}</td>
              <td class={invite.status}>{invite.status}</td>
              <td class="actions">
                <button type="button" on:click={() => copyToClipboard(invite.code, 'Invite code')} disabled={actionPending}>Copy code</button>
                <button type="button" on:click={() => copyToClipboard(inviteLink(invite), 'Invite link')} disabled={actionPending}>Copy link</button>
                <button type="button" on:click={() => handleRegenerate(invite)} disabled={actionPending}>Regenerate</button>
                <button type="button" on:click={() => handleRevoke(invite)} disabled={actionPending || invite.status === 'revoked'}>Revoke</button>
                {#if invite.status === 'revoked'}
                  <button type="button" class="ghost" on:click={() => handleDelete(invite)} disabled={actionPending}>Delete</button>
                {/if}
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    {/if}
  </div>

  <div class="invite-section">
    <h4>Personal invites</h4>
    {#if invitesState.loading && personalInvites.length === 0}
      <p>Loading invites…</p>
    {:else if personalInvites.length === 0}
      <p class="empty-text">No personal invites created yet.</p>
    {:else}
      <table>
        <thead>
          <tr>
            <th>Code</th>
            <th>Guest</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {#each personalInvites as invite (invite.id)}
            {@const person = invitePerson(invite)}
            <tr>
              <td><strong>{invite.code}</strong></td>
              <td>
                <div class="person-name">{person?.name ?? invite.targetNormalizedEmail ?? 'Guest'}</div>
                <div class="muted">{person?.email ?? invite.targetNormalizedEmail ?? 'No email stored'}</div>
              </td>
              <td class={invite.status}>{invite.status}</td>
              <td class="actions">
                <button type="button" on:click={() => copyToClipboard(inviteLink(invite), 'Invite link')} disabled={actionPending}>Copy link</button>
                <button type="button" on:click={() => handleRegenerate(invite)} disabled={actionPending}>Regenerate</button>
                <button type="button" on:click={() => handleRevoke(invite)} disabled={actionPending || invite.status === 'revoked'}>Revoke</button>
                {#if invite.status === 'revoked'}
                  <button type="button" class="ghost" on:click={() => handleDelete(invite)} disabled={actionPending}>Delete</button>
                {/if}
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    {/if}
  </div>
</section>

<style>
  .invite-manager {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 1.5rem;
    border-radius: 16px;
    background: rgba(18, 18, 24, 0.85);
    border: 1px solid rgba(255, 255, 255, 0.05);
  }

  header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
  }

  header h3 {
    margin: 0;
  }

  header p {
    margin: 0.25rem 0 0;
    color: rgba(255, 255, 255, 0.65);
    font-size: 0.9rem;
  }

  header button,
  .invite-form button,
  .actions button {
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: transparent;
    color: inherit;
    padding: 0.4rem 0.75rem;
    border-radius: 8px;
    cursor: pointer;
    font-size: 0.85rem;
  }

  .bulk-tools {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .bulk-tools button {
    border: none;
    border-radius: 12px;
    padding: 0.6rem 1rem;
    font-weight: 600;
    cursor: pointer;
    background: linear-gradient(135deg, rgba(240, 169, 74, 0.6), rgba(178, 127, 244, 0.4));
    color: #050608;
  }

  .bulk-tools button.ghost {
    background: rgba(255, 255, 255, 0.1);
    color: inherit;
  }

  .bulk-tools button:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  .invite-form {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.02);
  }

  label {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    font-size: 0.85rem;
  }

  select {
    padding: 0.5rem 0.75rem;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    background: rgba(0, 0, 0, 0.3);
    color: inherit;
  }

  select {
    min-width: 220px;
  }

  .cta {
    align-self: flex-start;
    background: rgba(255, 255, 255, 0.08);
  }

  .invite-section {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85rem;
  }

  th,
  td {
    padding: 0.6rem 0.75rem;
    text-align: left;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  th {
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.5);
  }

  td strong {
    letter-spacing: 0.05em;
  }

  td.actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
  }

  .person-name {
    font-weight: 600;
  }

  td.active {
    color: #6be2b3;
  }

  td.exhausted {
    color: #f1c453;
  }

  td.revoked {
    color: #ff8585;
  }

  .error-text {
    margin: 0;
    color: #ff8585;
    font-size: 0.85rem;
  }

  .notice-text {
    margin: 0;
    color: #6be2b3;
    font-size: 0.85rem;
  }

  .empty-text {
    margin: 0;
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.85rem;
  }
</style>
